import { Component, OnInit, inject, signal, effect, HostListener } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin, catchError, of } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { BiblioDetailService } from './BiblioDetailService';
import { BiblioDetailMapper } from './biblio-detail.mapper';
import { BiblioDetailModel } from '../../types/types';
import { BreadCrump } from "../../shared/bread-crump/bread-crump";
import { HoldService } from '../../core/services/hold.service';
import { SuggestionService } from '../../core/services/suggestion.service';
import { PatronAuthService } from '../../core/services/patron-auth.service';

import { FormsModule } from '@angular/forms';

type TabKey = 'book' | 'description' | 'copies' | 'marc' | 'isbd';

@Component({
  selector: 'app-index-details',
  standalone: true,
  imports: [CommonModule, RouterModule, DatePipe, BreadCrump, TranslateModule, FormsModule],
  templateUrl: './index-details.html',
  styleUrl: './index-details.scss',
})
export class IndexDetails implements OnInit {
  private route = inject(ActivatedRoute);
  private service = inject(BiblioDetailService);
  private holdService = inject(HoldService);
  private suggestionService = inject(SuggestionService);
  public patronAuthService = inject(PatronAuthService);

  /** Resolved biblio + holdings display model */
  biblio = signal<BiblioDetailModel | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);

  /** Active tab: 'book' | 'description' | 'copies' | 'marc' | 'isbd' */
  activeTab = signal<TabKey>('book');

  // --- Hold State ---
  isHolding = signal(false);
  currentHoldId = signal<number | null>(null);
  holdError = signal<string | null>(null);
  holdSuccess = signal<string | null>(null);

  // --- Suggestion State ---
  showSuggestModal = signal(false);
  isSuggesting = signal(false);
  suggestSuccess = signal(false);
  suggestError = signal<string | null>(null);
  suggestTitle = '';
  suggestAuthor = '';
  suggestNote = '';

  readonly tabs: { key: TabKey; label: string; icon?: string }[] = [
    { key: 'book',        label: 'indexDetails.tabs.holdings', icon: 'book' },
    // { key: 'copies',      label: 'indexDetails.tabs.copies', icon: 'copies' },
    { key: 'marc',        label: 'indexDetails.tabs.marc', icon: 'marc' },
  ];

  readonly fallbackImage = 'images/flag.svg';

  constructor() {
    effect(() => {
      const b = this.biblio();
      if (b) {
        this.fetchExistingHold(b.id);
      }
    });
  }

  getPatronId(): number | null {
    const patronDataStr = localStorage.getItem('patron_user');
    if (patronDataStr) {
      try {
        const parsed = JSON.parse(patronDataStr);
        return parsed.patron_id || parsed.id || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  private fetchExistingHold(biblioId: number): void {
    const patronId = this.getPatronId();
    if (!patronId) return;
    this.holdService.getHolds(patronId, biblioId).subscribe({
      next: (holds) => {
        if (holds && holds.length > 0) {
          this.currentHoldId.set(holds[0].hold_id || holds[0].id);
        } else {
          this.currentHoldId.set(null);
        }
      },
      error: () => this.currentHoldId.set(null)
    });
  }

  ngOnInit(): void {
    // Supports both route param (:id) and query param (?id=...)
    const paramId = this.route.snapshot.paramMap.get('id');
    const queryId = this.route.snapshot.queryParamMap.get('id');
    const rawId = paramId ?? queryId;

    if (!rawId) {
      this.error.set('indexDetails.missingRecordId');
      this.loading.set(false);
      return;
    }

    const id = +rawId;

    forkJoin({
      biblio: this.service.fetchBiblio(id),
      items: this.service.fetchItems(id),
      marc: this.service.fetchMarc(id).pipe(catchError(err => {
        console.error('MARC Error:', err);
        return of(null);
      })),
    }).subscribe({
      next: ({ biblio, items, marc }) => {
        this.biblio.set(BiblioDetailMapper.toModel(biblio, items, marc));
        this.loading.set(false);
      },
      error: () => {
        this.error.set('indexDetails.fetchError');
        this.loading.set(false);
      },
    });
  }

  setTab(key: TabKey): void {
    this.activeTab.set(key);
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = this.fallbackImage;
  }

  /** Map status value to translation key + CSS modifier */
  statusLabel(status: string): string {
    const map: Record<string, string> = {
      'available': 'indexDetails.statusMap.available',
      'checked-out': 'indexDetails.statusMap.checkedOut',
      'lost': 'indexDetails.statusMap.lost',
      'not-for-loan': 'indexDetails.statusMap.notForLoan',
    };
    return map[status] ?? status;
  }

  // --- Citation State ---
  showCiteModal = signal(false);
  citations = signal<{style: string, text: string}[]>([]);

  // --- Sidebar Actions ---

  placeHold(): void {
    if (!this.biblio()) return;
    this.holdError.set(null);
    this.holdSuccess.set(null);

    const patronId = this.getPatronId();
    if (!patronId) {
      this.holdError.set('يجب تسجيل الدخول أولاً لتتمكن من الحجز.');
      return;
    }
    const pickupLibrary = 'KAW';

    this.isHolding.set(true);
    this.holdService.placeHold({
      biblio_id: this.biblio()!.id,
      patron_id: patronId,
      pickup_library_id: pickupLibrary
    }).subscribe({
      next: (res) => {
        this.holdSuccess.set('تم حجز المادة بنجاح.');
        this.isHolding.set(false);
        this.currentHoldId.set(res.hold_id || res.id || 999);
      },
      error: (err) => {
        this.holdError.set(err.error?.message || err.error || err.message || 'فشل في عملية الحجز.');
        this.isHolding.set(false);
      }
    });
  }

  removeHold(): void {
    const holdId = this.currentHoldId();
    if (!holdId) return;
    
    this.holdError.set(null);
    this.holdSuccess.set(null);

    this.isHolding.set(true);
    this.holdService.cancelHold(holdId).subscribe({
      next: () => {
        this.holdSuccess.set('تم إلغاء الحجز بنجاح.');
        this.isHolding.set(false);
        this.currentHoldId.set(null);
      },
      error: (err) => {
        this.holdError.set(err.error?.message || err.error || err.message || 'فشل في إلغاء الحجز.');
        this.isHolding.set(false);
      }
    });
  }

  printRecord(): void {
    window.print();
  }

  citeRecord(): void {
    if (!this.biblio()) return;
    const b = this.biblio()!;
    
    // Helper to strip trailing ISBD punctuation from Koha data
    const clean = (str: string | undefined | null) => {
      if (!str) return '';
      return str.replace(/[.,\/;: ]+$/, '').trim();
    };

    const a = clean(b.author) || 'Unknown Author';
    const t = clean(b.title) || 'Unknown Title';
    const p = clean(b.publisher);
    const rawYear = (b as any).publicationYear || (b as any).copyrightDate || (b as any).date || '';
    // Strip everything except numbers for the year to be safe
    const y = clean(rawYear.toString().replace(/[^0-9]/g, ''));

    // Chicago / MLA format: Author. Title. Publisher, Year.
    const publisherAndYear = [p, y].filter(Boolean).join(', ');
    const chicagoMla = [a, t, publisherAndYear].filter(Boolean).join('. ') + '.';

    // APA format: Author. (Year). Title. Publisher.
    const apaYear = y ? `(${y}).` : '';
    const apa = [a + '.', apaYear, t + '.', p + '.'].filter(Boolean).join(' ').replace(/\.\./g, '.');

    // Harvard format: Author (Year) Title. Publisher.
    const harvardYear = y ? `(${y})` : '';
    const harvard = [a, harvardYear, t + '.', p + '.'].filter(Boolean).join(' ').replace(/\.\./g, '.');

    this.citations.set([
      { style: 'APA', text: apa },
      { style: 'Chicago', text: chicagoMla },
      { style: 'Harvard', text: harvard },
      { style: 'MLA', text: chicagoMla }
    ]);
    
    this.showCiteModal.set(true);
  }

  openSuggestModal(): void {
    this.suggestTitle = '';
    this.suggestAuthor = '';
    this.suggestNote = '';
    this.suggestSuccess.set(false);
    this.suggestError.set(null);
    this.showSuggestModal.set(true);
  }

  closeSuggestModal(): void {
    this.showSuggestModal.set(false);
    this.suggestSuccess.set(false);
    this.suggestError.set(null);
  }

  submitSuggestion(): void {
    const patronId = this.getPatronId();
    if (!patronId) return;

    if (!this.suggestTitle.trim()) {
      this.suggestError.set('الرجاء إدخال عنوان المادة');
      return;
    }

    this.isSuggesting.set(true);
    this.suggestError.set(null);

    const payload: any = {
      title: this.suggestTitle.trim(),
      suggested_by: patronId,
      library_id: 'KAW'
    };

    if (this.suggestAuthor.trim()) {
      payload.author = this.suggestAuthor.trim();
    }

    if (this.suggestNote.trim()) {
      payload.note = this.suggestNote.trim();
    }

    this.suggestionService.suggestPurchase(payload).subscribe({
      next: () => {
        this.isSuggesting.set(false);
        this.suggestSuccess.set(true);
      },
      error: (err) => {
        this.isSuggesting.set(false);
        this.suggestError.set(err.error?.message || err.error || err.message || 'Failed to submit suggestion.');
      }
    });
  }

  suggestPurchase(): void {
    this.openSuggestModal();
  }

  // --- Save / Export Record State ---
  showSaveDropdown = signal(false);

  toggleSaveDropdown(event: Event): void {
    event.stopPropagation();
    this.showSaveDropdown.update(v => !v);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showSaveDropdown()) {
      this.showSaveDropdown.set(false);
    }
  }

  private downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  exportBibTeX(): void {
    const b = this.biblio();
    if (!b) return;
    const key = `record_${b.id}`;
    const clean = (s?: string | null) => s ? s.replace(/[{}"]/g, '').trim() : '';
    const content = `@book{${key},
  title = {${clean(b.title)}},
  author = {${clean(b.author)}},
  publisher = {${clean(b.publisher)}},
  year = {${clean(b.publicationYear)}},
  isbn = {${clean(b.isbn)}},
  address = {${clean(b.publicationPlace)}}
}`;
    this.downloadFile(content, `${key}.bib`, 'application/x-bibtex;charset=utf-8');
    this.showSaveDropdown.set(false);
  }

  exportRIS(): void {
    const b = this.biblio();
    if (!b) return;
    const clean = (s?: string | null) => s ? s.replace(/[\r\n]/g, ' ').trim() : '';
    const lines = [
      'TY  - BOOK',
      `TI  - ${clean(b.title)}`,
      b.author ? `AU  - ${clean(b.author)}` : '',
      b.publisher ? `PB  - ${clean(b.publisher)}` : '',
      b.publicationYear ? `PY  - ${clean(b.publicationYear)}` : '',
      b.publicationPlace ? `CY  - ${clean(b.publicationPlace)}` : '',
      b.isbn ? `SN  - ${clean(b.isbn)}` : '',
      b.abstract ? `AB  - ${clean(b.abstract)}` : '',
      'ER  - '
    ].filter(Boolean).join('\r\n');
    this.downloadFile(lines, `record_${b.id}.ris`, 'application/x-research-info-systems;charset=utf-8');
    this.showSaveDropdown.set(false);
  }

  exportText(): void {
    const b = this.biblio();
    if (!b) return;
    const content = `العنوان: ${b.title || '—'}
المؤلف: ${b.author || '—'}
الناشر: ${b.publisher || '—'}
سنة النشر: ${b.publicationYear || '—'}
مكان النشر: ${b.publicationPlace || '—'}
الرقم الدولي المعياري (ISBN): ${b.isbn || '—'}
نوع المصدر: ${b.itemTypeLabel || b.itemType || '—'}
تصنيف ديوي / رقم الاستدعاء: ${b.callNumber || '—'}
المستخلص: ${b.abstract || '—'}
`;
    this.downloadFile(content, `record_${b.id}.txt`, 'text/plain;charset=utf-8');
    this.showSaveDropdown.set(false);
  }

  exportMarcJson(): void {
    const b = this.biblio();
    if (!b) return;
    const content = JSON.stringify(b, null, 2);
    this.downloadFile(content, `record_${b.id}.json`, 'application/json;charset=utf-8');
    this.showSaveDropdown.set(false);
  }


}
